import { StoryBoard } from 'services/storyboard.service';
import { RootState } from 'store/store';
import { useAppDispatch, useAppStore } from 'store/store-hooks';
import { createClipId, getSbPosition, getSbTracks } from 'components/app/_helpers/_app.helper';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { isImage } from 'components/app/storyboard/sb-player/_mediator/sb-player.helper';
import { Clip, EMPTY_CELL, HandleClips, handleClips } from 'components/app/editor/_mediator/editor.slice';
import { useEditorCb } from 'components/app/editor/_mediator/use-editor-cb';
import { BackupParams } from 'components/app/editor/_mediator/backup/backup.slice';
import { useMarkersCb } from 'components/app/editor/_mediator/editor-markers/use-markers-cb';
import { ResizePosition } from 'components/app/storyboard/sb-cell-resize/use-cell-resize-payload';
import { clipsApi } from './editor-clips.helper';

export const useResize = (clip?: Clip | null) => {
  const { wsGetCursorTime } = useAudioTrackContext();
  const { addMarkers, delMarkers } = useMarkersCb();
  const { getPlayerRef } = useEditorCb();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const addNewMarker = (position: ResizePosition) => {
    const { track } = selectData(store.getState());
    const wsTime = wsGetCursorTime();
    const res = addMarkers([wsTime], { dispatch: false });
    if (!res) return;
    if (position === 'left') {
      const sb = res.storyBoard;
      const clipId = sb[track][0];
      sb[track][0] = EMPTY_CELL;
      sb[track][1] = clipId;
    }
    return res;
  };

  const updateLeftMarkers = (snapTo: Index | null, straddledMarkers: Index[], draggingMarker: Index) => {
    const { markers, storyBoard } = selectData(store.getState());
    if (!markers) return;
    let _res = { markers: [...markers], storyBoard };
    if (snapTo === -1) {
      straddledMarkers.push((straddledMarkers.at(-1) ?? -1) + 1);
      const res = addMarkers([0], { markers: [...markers], dispatch: false, handleError: false, fixLinks: false })!;
      const sb = res.storyBoard;
      getSbTracks().forEach((track) => {
        const clipId = sb[track][0];
        sb[track][0] = EMPTY_CELL;
        sb[track][1] = clipId;
      });
      _res = res;
    }
    const res =
      delMarkers({
        selectedMarkers: straddledMarkers,
        storyBoard: _res.storyBoard,
        markers: _res.markers,
        dispatch: false,
      }) ?? _res;
    if (snapTo === -1) {
      getSbTracks().forEach((track) => res.storyBoard[track].shift());
      res.markers.shift();
    } else {
      const time = snapTo === null ? wsGetCursorTime() : markers[snapTo];
      const draggingMarkerIndex = res.markers.findIndex((marker) => marker === markers[draggingMarker]);
      res.markers[draggingMarkerIndex] = time;
    }
    return res;
  };

  const updateRightMarkers = (
    snapTo: Index | null,
    straddledMarkers: Index[],
    innerMarkers: MarkerId[],
    draggingMarker: Index
  ) => {
    const { markers, storyBoard } = selectData(store.getState());
    const selectedMarkers = [draggingMarker, ...straddledMarkers];
    const wsTime = wsGetCursorTime();
    if (!markers) return;

    if (snapTo === -1) {
      return delMarkers({
        selectedMarkers,
        storyBoard,
        markers,
        dispatch: false,
      });
    }
    if (straddledMarkers.length === 0) {
      const res = { markers: [...markers], storyBoard };
      if (innerMarkers.length === 0) res.markers[draggingMarker] = wsTime;
      return res;
    }
    if (snapTo === null) {
      const res = addMarkers([wsTime], { dispatch: false, fixLinks: false });
      if (!res) return;
      return delMarkers({
        selectedMarkers,
        storyBoard: res.storyBoard,
        markers: res.markers,
        dispatch: false,
      });
    }
    selectedMarkers.pop();
    return delMarkers({ selectedMarkers, dispatch: false });
  };

  const getClipsPayload = (position: ResizePosition, markers: MarkerId[], sb: StoryBoard) => {
    const { selector, linkedClips } = selectData(store.getState());
    if (!clip || !selector) return;
    if (isImage(clip.clipId)) return;

    const playerTime = getPlayerRef()?.getCurrentTime();
    if (playerTime === undefined) return;

    const newClip = createNewClip(playerTime, position, clip);
    if (!newClip) return;

    const res = clipsApi(selector, sb, linkedClips).del([clip]);
    const res1 = clipsApi(res.selector, res.storyBoard, res.linkedClips).add([newClip], getSbPosition(clip.clipId, sb));

    return {
      ...res1,
      storyBoard: { ...res.storyBoard, ...res1.storyBoard },
      clipsMap: { ...res.clipsMap, ...res1.clipsMap },
      markers,
    };
  };

  return (
    position: ResizePosition,
    draggingMarker: Index | null,
    snapTo: Index | null,
    straddledMarkers: Index[],
    innerMarkers: MarkerId[]
  ) => {
    const res =
      draggingMarker === null
        ? addNewMarker(position)
        : position === 'left'
        ? updateLeftMarkers(snapTo, straddledMarkers, draggingMarker)
        : updateRightMarkers(snapTo, straddledMarkers, innerMarkers, draggingMarker);
    if (!res) return;

    const markersPayload = addMarkers(
      draggingMarker !== null && position === 'right' && innerMarkers.length
        ? [wsGetCursorTime(), ...innerMarkers.slice(0, -1)]
        : innerMarkers,
      { ...res, dispatch: false }
    );
    if (!markersPayload) return;

    const clipsPayload = getClipsPayload(position, markersPayload.markers, markersPayload.storyBoard);
    const newClipId = clipsPayload === undefined ? clip!.clipId : Object.values(clipsPayload.clipsMap)[1]!.clipId;
    const backupParams: BackupParams = {
      title: 'Clip Resize',
      action: position === 'left' ? undefined : 'resizeRight',
      activeClip: [clip!.clipId, newClipId],
    };
    const data: HandleClips = { ...(clipsPayload ?? markersPayload) };
    dispatch(handleClips({ data, backupParams }));
  };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { duration } = state.audio;
  const { markers, storyBoard, selector, linkedClips, clipsMap } = state.editor;
  const { sbPosition } = state.editor__controls;
  const { sbClipPlaybackMap } = state.sb;
  return { duration, markers, storyBoard, selector, linkedClips, track: sbPosition.track, sbClipPlaybackMap, clipsMap };
};

/* H E L P E R S */

const createNewClip = (playerTime: Seconds, position: ResizePosition, clip: Clip): Clip | null => {
  let newClip;
  if (position === 'left' && clip.startTime !== null) newClip = { ...clip, startTime: playerTime };
  else if (position === 'right' && clip.endTime !== null) newClip = { ...clip, endTime: playerTime };
  if (newClip) newClip.clipId = createClipId(newClip);
  return newClip ?? null;
};
